import { useState } from "react"
import { useNavigate } from "@tanstack/react-router"

import type { SuggestionRow } from "../helpCenter/use-article-suggestions"
import type { DocsSaves } from "./docs-change"
import type { DocsGroupState } from "./use-recommendation-actions"
import { toast } from "@/components/toast"
import { captureError } from "@/lib/capture-error"
import { useAuthData, useZero } from "@/lib/zero"
import {
	acceptDocsGroup,
	rejectDocsGroup,
	reopenProposals,
} from "./use-recommendation-actions"

// Accept and decline for an AI doc suggestion that no recommendation owns.
export function useDocsSuggestionActions({
	proposalGroupId,
	group,
	rows,
	saves,
	onClosed,
}: {
	// Null for a teammate's suggestion or an API draft outside any group.
	proposalGroupId: string | null
	group: DocsGroupState | null | undefined
	rows: SuggestionRow[]
	saves: DocsSaves
	onClosed: () => void
}) {
	const zero = useZero()
	const navigate = useNavigate()
	const authData = useAuthData()
	const [busy, setBusy] = useState(false)
	const proposals = rows.flatMap((row) => row.proposals)

	const report = (error: unknown, action: string) => {
		captureError(error, {
			domain: "general",
			action,
			workspaceId: authData.workspaceId,
			userId: authData.userId,
			extra: { proposalGroupId },
		})
	}

	const accept = async () => {
		if (busy || proposals.length === 0) return
		setBusy(true)
		try {
			if (!(await saves.settle())) return
			const { newArticleId } = await acceptDocsGroup({ zero, group, rows })
			toast.success({ title: "Docs updated" })
			if (newArticleId)
				void navigate({ to: "/edit-docs/$id", params: { id: newArticleId } })
			else onClosed()
		} catch (error) {
			report(error, "accept agent docs suggestion")
			toast.error({ title: "Couldn't apply this change" })
		} finally {
			setBusy(false)
		}
	}

	const decline = async (reason: string, note: string) => {
		if (busy) return
		setBusy(true)
		const ids = proposals.map((p) => p.id)
		try {
			await rejectDocsGroup({
				zero,
				proposalGroupId,
				proposalIds: ids,
				reason,
				note,
			})
			toast.success({
				title: "Declined",
				action: {
					label: "Undo",
					callback: () =>
						reopenProposals(zero, ids, (error) =>
							report(error, "undo agent docs suggestion decline"),
						),
				},
			})
			onClosed()
		} catch (error) {
			report(error, "decline agent docs suggestion")
			toast.error({ title: "Couldn't decline this suggestion" })
		} finally {
			setBusy(false)
		}
	}

	return { accept, decline, busy }
}
